// src/pages/CanteenDashboard.js
// C1 fix: getUser() → getSession() (no network request)
// C2 fix: Realtime channel securely scoped to canteen_id
// C3 fix: setTimeout for latestAlert tracked in ref, cleared on unmount
// C4 fix: Explicit column list on orders and order_items
// Q2 fix (NEW): Authoritative authorization via `staff_profiles` table (No JWT metadata reliance)

import { useEffect, useState, useRef, useMemo } from 'react'
import { ArrowLeft, ChefHat, LogOut, XCircle, Flame, CheckCircle, Layers, Menu as MenuIcon, BellRing } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { getCanteenById, getMenuByCanteen } from '../lib/menuData'
import { requestFCMToken, setupForegroundListener } from '../lib/firebase'
import { motion, AnimatePresence } from 'framer-motion'

const NEXT_STATUS = { CONFIRMED: 'READY', READY: 'PICKED_UP' }

// Explicit column selection for orders — never select('*') on payment tables
const ORDER_SELECT = 'id, token_number, status, total_amount, pickup_slot, created_at, order_items(id, name, quantity)'

export default function CanteenDashboard({ onNavigate }) {
  const [staffUser,   setStaffUser]   = useState(null)
  const [canteenId,   setCanteenId]   = useState(null) // Q2 Fix: State for authoritative canteen_id
  const [orders,      setOrders]      = useState([])
  const [overrides,   setOverrides]   = useState(new Set())
  const [activeTab,   setActiveTab]   = useState('prep')
  const [firstLoad,   setFirstLoad]   = useState(true)
  const [updating,    setUpdating]    = useState(null)
  const [latestAlert, setLatestAlert] = useState(null)
  const [authErr,     setAuthErr]     = useState(null)

  const alertTimerRef = useRef(null)
  const activeTabRef = useRef(activeTab)

  const canteen    = canteenId ? getCanteenById(canteenId) : null
  const menuItems  = canteenId ? getMenuByCanteen(canteenId) : []

  useEffect(() => { activeTabRef.current = activeTab }, [activeTab])

  const showAlert = (alert) => {
    clearTimeout(alertTimerRef.current)
    setLatestAlert(alert)
    alertTimerRef.current = setTimeout(() => setLatestAlert(null), 6000)
  }

  const loadData = async (tab, silent = false) => {
    if (!canteenId) return
    if (!silent) setFirstLoad(true)

    if (tab === 'prep' || tab === 'batch') {
      const { data, error } = await supabase.from('orders')
        .select(ORDER_SELECT)
        .eq('canteen_id', canteenId).eq('status', 'CONFIRMED')
        .order('created_at', { ascending: true })
      if (error) console.error("Prep Fetch Error:", error)
      if (data) setOrders(data)
    } else if (tab === 'pack') {
      const { data, error } = await supabase.from('orders')
        .select(ORDER_SELECT)
        .eq('canteen_id', canteenId).eq('status', 'READY')
        .order('created_at', { ascending: true })
      if (error) console.error("Pack Fetch Error:", error)
      if (data) setOrders(data)
    } else if (tab === 'menu') {
      const { data } = await supabase.from('item_overrides')
        .select('item_id').eq('canteen_id', canteenId)
      if (data) setOverrides(new Set(data.map(row => row.item_id)))
    }
    setFirstLoad(false)
  }

  useEffect(() => {
    if (staffUser && canteenId) loadData(activeTab)
  }, [activeTab, staffUser, canteenId])

  // Initial Session Check
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const user = session?.user
      if (user) {
        // Q2 Fix: Authoritative Profile Check on hard refresh
        const { data: profile } = await supabase
          .from('staff_profiles')
          .select('canteen_id')
          .eq('user_id', user.id)
          .maybeSingle()

        if (profile?.canteen_id) {
          setStaffUser(user)
          setCanteenId(profile.canteen_id)
        } else {
          setFirstLoad(false) // Not a staff member
        }
      } else {
        setFirstLoad(false) // Not logged in
      }
    })
  }, [])

  useEffect(() => {
    if (!staffUser || !canteenId) return

    const initNotifications = async () => {
      const token = await requestFCMToken()
      if (token) {
        await supabase.from('canteen_devices').upsert({
          user_id: staffUser.id, canteen_id: canteenId,
          fcm_token: token, updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' })
      }

      setupForegroundListener((payload) => {
        showAlert({ title: payload.notification?.title, body: payload.notification?.body })
        if (Notification.permission === 'granted') {
          new Notification(payload.notification?.title || "New Order", { body: payload.notification?.body })
        }
        loadData(activeTabRef.current, true)
      })
    }
    initNotifications()

    // Realtime channel
    const channel = supabase.channel(`db-${canteenId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'orders', filter: `canteen_id=eq.${canteenId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE' &&
              payload.old.status === 'PENDING' &&
              payload.new.status === 'CONFIRMED') {
            showAlert({ title: `New Order #${payload.new.token_number}!`, body: `₹${payload.new.total_amount} paid.` })
          }
          loadData(activeTabRef.current, true)
        }
      )
      .subscribe()

    const handleVisibility = () => {
      if (!document.hidden) loadData(activeTabRef.current, true)
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      supabase.removeChannel(channel)
      document.removeEventListener('visibilitychange', handleVisibility)
      clearTimeout(alertTimerRef.current)
    }
  }, [staffUser?.id, canteenId])

  const batchItems = useMemo(() => {
    if (activeTab !== 'batch') return []
    const counts = {}
    orders.forEach(order => {
      order.order_items?.forEach(item => {
        counts[item.name] = (counts[item.name] || 0) + item.quantity
      })
    })
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
  }, [orders, activeTab])

  const toggleStock = async (itemId, isAvailable) => {
    setUpdating(itemId)
    if (isAvailable) {
      await supabase.from('item_overrides').upsert({ item_id: itemId, canteen_id: canteenId, is_available: false })
    } else {
      await supabase.from('item_overrides').delete().eq('item_id', itemId).eq('canteen_id', canteenId)
    }
    await loadData('menu', true)
    setUpdating(null)
  }

  const updateStatus = async (order) => {
    const next = NEXT_STATUS[order.status]
    if (!next) return
    setUpdating(order.id)
    await supabase.from('orders').update({ status: next }).eq('id', order.id)
    await loadData(activeTab, true)
    setUpdating(null)
  }

  const handleCancel = async (order) => {
    if (!window.confirm("Cancel this order? This triggers an automatic refund.")) return
    setUpdating(order.id)
    const { error } = await supabase.rpc('cancel_and_refund_order', {
      p_order_id: order.id,
      p_reason: "Canteen cancelled - Out of Stock"
    })
    if (error) alert("Error: " + error.message)
    else loadData(activeTab, true)
    setUpdating(null)
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setStaffUser(null)
    setCanteenId(null)
  }

  const isOrderNew = (createdAt) =>
    (Date.now() - new Date(createdAt).getTime()) / 1000 < 60

  if (authErr) {
    return (
      <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-2xl max-w-sm w-full">
          <h2 className="text-red-400 font-bold mb-2">Access Error</h2>
          <p className="text-gray-400 text-sm mb-6">{authErr}</p>
          <button onClick={handleLogout} className="bg-red-500/20 text-red-400 font-bold py-3 px-6 rounded-xl w-full active:scale-95">
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  if (firstLoad || !staffUser || !canteenId) return (
    <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 border-4 border-[#1c1c1e] border-t-[#f06e28] rounded-full animate-spin shadow-[0_0_15px_rgba(240,110,40,0.5)]" />
      <p className="text-gray-400 font-bold tracking-widest uppercase text-sm">Loading KDS...</p>
    </div>
  )
  
  if (firstLoad) return (
    <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 border-4 border-[#1c1c1e] border-t-[#f06e28] rounded-full animate-spin shadow-[0_0_15px_rgba(240,110,40,0.5)]" />
      <p className="text-gray-400 font-bold tracking-widest uppercase text-sm">Loading KDS...</p>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-sans text-gray-100 selection:bg-[#f06e28] selection:text-white relative">

      <AnimatePresence>
        {latestAlert && (
          <motion.div
            initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 left-4 right-4 z-[100] bg-[#f06e28] rounded-2xl p-4 shadow-[0_10px_30px_rgba(240,110,40,0.5)] border border-white/20 flex items-center gap-4"
          >
            <div className="bg-white/20 p-3 rounded-full animate-bounce">
              <BellRing className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-black text-white text-lg tracking-wide">{latestAlert.title}</h3>
              <p className="text-white/80 font-bold text-sm">{latestAlert.body}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-[#121212]/95 backdrop-blur-2xl sticky top-0 z-50 border-b border-white/10 shadow-xl pb-2">
        <div className="max-w-lg mx-auto px-4 pt-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => onNavigate('profile')} className="bg-[#1c1c1e] p-2.5 rounded-xl border border-white/5 shadow-sm active:scale-95">
                <ArrowLeft className="w-5 h-5 text-gray-200" />
              </button>
              <div>
                <h1 className="font-heading font-extrabold tracking-wide text-xl text-white leading-none">{canteen?.shortName || canteenId} KDS</h1>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                  <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Live</p>
                </div>
              </div>
            </div>
            <button onClick={handleLogout}
              className="bg-red-500/10 text-red-400 p-2.5 rounded-xl active:scale-95">
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            <button onClick={() => setActiveTab('prep')} className={`flex items-center gap-2 flex-shrink-0 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'prep' ? 'bg-[#f06e28] text-white shadow-[0_4px_10px_-2px_rgba(240,110,40,0.4)]' : 'bg-[#1c1c1e] text-gray-500 border border-white/5'}`}>
              <Flame className="w-3.5 h-3.5" /> Prep
            </button>
            <button onClick={() => setActiveTab('pack')} className={`flex items-center gap-2 flex-shrink-0 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'pack' ? 'bg-green-500 text-white shadow-[0_4px_10px_-2px_rgba(34,197,94,0.4)]' : 'bg-[#1c1c1e] text-gray-500 border border-white/5'}`}>
              <CheckCircle className="w-3.5 h-3.5" /> Pack
            </button>
            <button onClick={() => setActiveTab('batch')} className={`flex items-center gap-2 flex-shrink-0 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'batch' ? 'bg-blue-500 text-white shadow-[0_4px_10px_-2px_rgba(59,130,246,0.4)]' : 'bg-[#1c1c1e] text-gray-500 border border-white/5'}`}>
              <Layers className="w-3.5 h-3.5" /> Batch
            </button>
            <button onClick={() => setActiveTab('menu')} className={`flex items-center gap-2 flex-shrink-0 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'menu' ? 'bg-purple-500 text-white shadow-[0_4px_10px_-2px_rgba(168,85,247,0.4)]' : 'bg-[#1c1c1e] text-gray-500 border border-white/5'}`}>
              <MenuIcon className="w-3.5 h-3.5" /> 86's
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 pb-12">

        {activeTab === 'prep' && (
          <div className="space-y-4">
            {orders.length === 0 && <p className="text-gray-600 font-black text-center py-16 uppercase tracking-widest text-sm">No Orders to Cook</p>}
            {orders.map(order => {
              const isNew = isOrderNew(order.created_at)
              return (
                <div key={order.id} className={`bg-[#1c1c1e] rounded-2xl border-l-4 border-l-[#f06e28] border-y border-r border-white/5 p-4 shadow-lg transition-all ${isNew ? 'ring-2 ring-[#f06e28]/50 shadow-[0_0_20px_rgba(240,110,40,0.2)]' : ''}`}>
                  <div className="flex justify-between items-start mb-4 border-b border-white/5 pb-4">
                    <div className="flex gap-3 items-center">
                      <div className="bg-[#f06e28]/10 text-[#f06e28] font-heading font-black text-2xl px-3 py-1 rounded-lg border border-[#f06e28]/20">#{order.token_number}</div>
                      {isNew
                        ? <div className="px-2 py-1 bg-[#f06e28] text-white rounded text-[10px] font-black uppercase tracking-widest animate-pulse shadow-md">🔥 New</div>
                        : <div className="px-2 py-1 bg-white/5 rounded text-[10px] font-bold text-gray-400 uppercase tracking-widest">{order.pickup_slot}</div>
                      }
                    </div>
                    <button onClick={() => handleCancel(order)} disabled={updating === order.id} className="text-red-500/50 hover:text-red-400 p-1">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-2 mb-4 px-1">
                    {order.order_items?.map(i => (
                      <div key={i.id} className="flex items-start text-lg font-bold text-gray-100 tracking-wide">
                        <span className="text-[#f06e28] font-black mr-3">{i.quantity}x</span>
                        <span>{i.name}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => updateStatus(order)} disabled={updating === order.id}
                    className="w-full bg-[#f06e28] text-white py-4 rounded-xl font-black text-xs uppercase tracking-widest shadow-[0_8px_20px_-4px_rgba(240,110,40,0.3)] active:scale-[0.98] transition-transform">
                    {updating === order.id ? 'Moving...' : 'Send to Pack ➔'}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {activeTab === 'pack' && (
          <div className="space-y-4">
            {orders.length === 0 && <p className="text-gray-600 font-black text-center py-16 uppercase tracking-widest text-sm">No Orders to Pack</p>}
            {orders.map(order => (
              <div key={order.id} className="bg-[#1c1c1e] rounded-2xl border-l-4 border-l-green-500 border-y border-r border-white/5 p-4 shadow-lg">
                <div className="flex justify-between items-start mb-3 border-b border-white/5 pb-3">
                  <div className="bg-green-500/10 text-green-400 font-heading font-black text-2xl px-3 py-1 rounded-lg border border-green-500/20">#{order.token_number}</div>
                  <div className="px-2 py-1 bg-white/5 rounded text-[10px] font-bold text-gray-400 uppercase tracking-widest">{order.pickup_slot}</div>
                </div>
                <div className="space-y-1 mb-4 text-sm font-medium text-gray-300">
                  {order.order_items?.map(i => (
                    <div key={i.id} className="truncate"><span className="text-gray-500 mr-2">{i.quantity}x</span>{i.name}</div>
                  ))}
                </div>
                <button onClick={() => updateStatus(order)} disabled={updating === order.id}
                  className="w-full bg-[#121212] text-green-400 border border-green-500/30 py-4 rounded-xl font-black text-xs uppercase tracking-widest active:scale-95 transition-transform">
                  {updating === order.id ? 'Clearing...' : '✔ Handed to Student'}
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'batch' && (
          <div className="bg-[#1c1c1e] rounded-[24px] border border-blue-500/20 shadow-[0_0_20px_rgba(59,130,246,0.1)] p-5">
            <h2 className="font-heading font-black text-lg text-white mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" /> Total Items to Cook
            </h2>
            {batchItems.length === 0
              ? <p className="text-gray-600 font-black text-center py-10 uppercase tracking-widest text-sm">Nothing to Cook</p>
              : (
                <div className="divide-y divide-white/5">
                  {batchItems.map(([name, qty]) => (
                    <div key={name} className="flex justify-between items-center py-3">
                      <span className="font-bold text-gray-200 text-lg">{name}</span>
                      <span className="bg-blue-500 text-white font-black text-lg px-4 py-1 rounded-lg border border-blue-400 shadow-md">{qty}</span>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}

        {activeTab === 'menu' && (
          <div className="bg-[#1c1c1e] rounded-[24px] border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.1)] overflow-hidden">
            <div className="p-4 border-b border-white/5 bg-[#121212]/50">
              <h2 className="font-heading font-black text-white flex items-center gap-2">
                <MenuIcon className="w-5 h-5 text-purple-400" /> Stock Control
              </h2>
            </div>
            <div className="divide-y divide-white/5">
              {menuItems.map(item => {
                const isAvailable = !overrides.has(item.id)
                return (
                  <div key={item.id} className="flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center gap-3">
                      <img src={item.image} className={`w-12 h-12 rounded-lg object-cover ${!isAvailable ? 'grayscale opacity-50' : ''}`} alt={item.name} />
                      <div>
                        <p className={`font-bold tracking-wide text-sm ${isAvailable ? 'text-gray-100' : 'text-gray-500'}`}>{item.name}</p>
                        <p className={`text-[9px] font-black tracking-widest uppercase ${isAvailable ? 'text-green-400' : 'text-red-400'}`}>
                          {isAvailable ? '● Active' : '● Depleted (86)'}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => toggleStock(item.id, isAvailable)} disabled={updating === item.id}
                      className={`px-4 py-2.5 rounded-lg text-[10px] font-black tracking-widest uppercase border active:scale-95 ${isAvailable ? 'bg-[#121212] text-red-400 border-red-500/30' : 'bg-green-500/10 text-green-400 border-green-500/30'}`}>
                      {updating === item.id ? 'Wait' : isAvailable ? '86 Item' : 'Revive'}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}